import type { FC } from "react";
import React, { memo, useId } from "react";

import { Controller, useFormContext } from "@ui/components/Form";
import { InputEditButton, TextInput } from "@ui/components/Inputs";
import { InputLabel } from "@ui/components/Inputs/components/InputWrapper/InputLabel";
import { Select } from "@ui/components/Inputs/Select/Select";
import { useTranslations } from "@ui/providers/useTranslations";
import { getCountriesCodesArray } from "@ui/utils/countryCode/countryCode";
import { validationPatterns } from "@ui/utils/validationPatterns";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { SupportingText } from "@jti/core-components";

import styles from "./RegularTelephone.module.scss";

export type RegularTelephoneProps = {
  showLabel?: boolean;
  fieldName?(name: string): string;
  isRequired?: boolean;
  isWithoutDropdown?: boolean;
  isDisabled?: boolean;
  isInfoMessageVisible?: boolean;
  isPhoneFieldFullWidth?: boolean;
  autoComplete?: string;
  mode?: "standard" | "editable";
  editButtonLabel?: string;
  onEditClick?: () => void;
};

export const validatePhone = (e: React.KeyboardEvent<HTMLInputElement>): boolean | undefined => {
  const isNotNumber = isNaN(Number(e.key));
  const specialKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Enter", "Tab"];
  const isKeyAllowed = specialKeys.includes(e.code) || specialKeys.includes(e.key);
  const isKeyNotAllowed = e.code === "Space" || e.key === " ";

  if (isKeyAllowed) {
    return true;
  }

  if (isNotNumber || isKeyNotAllowed) {
    e.preventDefault();
    return false;
  }
};

const onBeforeInputHandler = (event: React.FormEvent<HTMLInputElement>) => {
  const inputValue = (event.nativeEvent as InputEvent).data;
  const isNotNumber = isNaN(Number(inputValue));

  if (isNotNumber) {
    event.preventDefault();
  }
};

export const RegularTelephone: FC<RegularTelephoneProps> = memo(
  ({
    showLabel = true,
    fieldName = (name: string) => name,
    isRequired = true,
    isWithoutDropdown,
    isDisabled = false,
    isInfoMessageVisible = false,
    isPhoneFieldFullWidth,
    autoComplete,
    mode = "standard",
    editButtonLabel,
    onEditClick,
  }) => {
    const { __ } = useTranslations();
    const config = useConfig();
    const isEditableMode = mode === "editable";
    const { getValues, control } = useFormContext();
    const formatHintId = useId();
    const DEFAULT_PHONE_NUMBER_LENGTH = 12;
    const prefixLength = getValues(fieldName("code"))?.value?.length || 0;
    const maxPhoneNumber = +config.PHONE_NUMBER_MAX_LENGTH || DEFAULT_PHONE_NUMBER_LENGTH;
    const minPhoneNumber = +config.PHONE_NUMBER_MIN_LENGTH || DEFAULT_PHONE_NUMBER_LENGTH;
    const pattern = {
      pattern: {
        message: __("form_validation.phone_number_pattern"),
        value: validationPatterns.PHONE_NUMBER,
      },
      minLength: {
        value: minPhoneNumber - prefixLength,
        message: __("form_validation.phone_number_pattern"),
      },
      maxLength: {
        value: maxPhoneNumber - prefixLength,
        message: __("form_validation.phone_number_pattern"),
      },
    };
    const validationRules = isRequired
      ? {
          ...pattern,
          required: __("form_validation.field_required"),
        }
      : pattern;

    const countriesCodesArray = React.useMemo(() => getCountriesCodesArray(config), [config]);

    const renderPrefixDropdown = () => {
      if (!isWithoutDropdown) {
        return (
          <div className={styles.code}>
            <Controller
              control={control}
              defaultValue={countriesCodesArray[0]}
              name={fieldName("code")}
              render={({ field }) => (
                <Select
                  {...field}
                  dataTestId="prefixDropdown"
                  isSearchable={false}
                  aria-label={__("internationalPhone.country_code_aria_label")}
                  placeholder={__("contact_info.phone_code_placeholder")}
                  options={countriesCodesArray}
                  isDisabled={isDisabled || isEditableMode}
                />
              )}
            />
          </div>
        );
      }
      return null;
    };

    const maxPhoneNumberLength = !isWithoutDropdown ? maxPhoneNumber - prefixLength : undefined;
    const label = isWithoutDropdown
      ? __("contact_info.phone_number_without_dropdown")
      : __("contact_info.phone_number");

    return (
      <div className={styles.phoneSelectBlock}>
        <div className={styles.phoneContainer}>
          {showLabel && (
            <InputLabel id="phoneLabelId" htmlFor="phoneLabelId">{`${label} ${isRequired ? "*" : ""}`}</InputLabel>
          )}
          <div className={styles.control}>
            {renderPrefixDropdown()}
            <div
              className={classNames({ [styles.telephoneFullWidth]: isWithoutDropdown || isPhoneFieldFullWidth })}
              onKeyDown={validatePhone}
            >
              <Controller
                control={control}
                name={fieldName("phone")}
                render={({ field, fieldState }) => (
                  <TextInput
                    {...field}
                    labelId="phoneLabelId"
                    aria-required={isRequired || undefined}
                    aria-describedby={isInfoMessageVisible ? formatHintId : undefined}
                    data-testid="phoneInput"
                    placeholder={__("contact_info.phone_placeholder")}
                    type="text"
                    maxLength={maxPhoneNumberLength}
                    disabled={isDisabled}
                    isError={!!fieldState?.error}
                    errorText={fieldState?.error?.message}
                    onBeforeInput={onBeforeInputHandler}
                    autoComplete={autoComplete}
                    readOnly={isEditableMode}
                    elemAfter={
                      isEditableMode && onEditClick ? (
                        <InputEditButton label={editButtonLabel} onClick={onEditClick} />
                      ) : undefined
                    }
                  />
                )}
                rules={validationRules}
                defaultValue=""
              />
            </div>
          </div>
          {isInfoMessageVisible && (
            <SupportingText id={formatHintId} data-testid="textInputInfoIcon">
              {__("profile.phone_update_page.otp.input.format")}
            </SupportingText>
          )}
        </div>
      </div>
    );
  }
);
